import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Chip,
  Avatar,
  Stack,
  Alert,
  Tooltip,
  Backdrop,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CompareArrows as CompareArrowsIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  ArrowForward as ArrowForwardIcon,
  SportsBasketball as BasketballIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import api from '../../api/axiosConfig';
import { useTheme } from '@mui/material/styles';
import { getPlayerPhotoUrl } from '../../utils/mediaUrl';
import { getCompGroup } from '../../utils/competitionUtils';
import { usePositions } from '../../utils/positions';
import { translateEurobasketPos, isLegacyPositionFilter } from './eurobasketPositions';

const LoadingBackdrop = ({ open }) => (
  <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.drawer + 9999 }}>
    <CircularProgress color="inherit" />
  </Backdrop>
);

const STORAGE_KEY = 'compPerformance_state';

function loadSavedState() {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
}

function CompetitionPerformance() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { positionsWithKeys } = usePositions(t);

  // Buttons: "All" + the 5 app positions (translated to current language).
  // Value = app position key ('pointGuard'...) or null for "All".
  const POSITIONS = React.useMemo(() => [
    { value: null, label: t('competitionPerformance.all', 'All') },
    ...positionsWithKeys.map(p => ({ value: p.key, label: p.label })),
  ], [positionsWithKeys, t]);

  const saved = React.useRef(loadSavedState()).current;
  // Discard legacy bucket-format filter values ('G', 'G/F', 'F', 'F/C', 'C')
  // so the toggle group doesn't end up with an unselectable value.
  const initialPositionFilter = isLegacyPositionFilter(saved?.positionFilter)
    ? null
    : (saved?.positionFilter ?? null);

  const [apiCompetitions, setApiCompetitions] = useState([]);
  const [originCompetition, setOriginCompetition] = useState(null);
  const [destinationCompetition, setDestinationCompetition] = useState(null);
  const [positionFilter, setPositionFilter] = useState(initialPositionFilter);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCompetitions, setLoadingCompetitions] = useState(true);
  const [error, setError] = useState(null);

  // Save state to sessionStorage on changes
  useEffect(() => {
    const state = {
      originId: originCompetition?.id || null,
      destinationId: destinationCompetition?.id || null,
      positionFilter,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [originCompetition, destinationCompetition, positionFilter]);

  useEffect(() => {
    loadApiCompetitions();
  }, []);

  const loadApiCompetitions = async () => {
    try {
      setLoadingCompetitions(true);
      const res = await api.get('/api/comp/api-competitions', { params: { source: 'eurobasket' } });
      const comps = res.data || [];
      setApiCompetitions(comps);

      // Restore saved selections
      if (saved && comps.length > 0) {
        if (saved.originId) {
          const o = comps.find(c => c.id === saved.originId);
          if (o) setOriginCompetition(o);
        }
        if (saved.destinationId) {
          const d = comps.find(c => c.id === saved.destinationId);
          if (d) setDestinationCompetition(d);
        }
      }
    } catch (err) {
      console.error('Error loading API competitions:', err);
      setError(t('competitionPerformance.errors.loadingCompetitions'));
    } finally {
      setLoadingCompetitions(false);
    }
  };

  const fetchResults = useCallback(async (pos) => {
    if (!originCompetition || !destinationCompetition) return;
    try {
      setLoading(true);
      setError(null);
      setResults(null);
      const res = await api.post('/api/comp/performance', {
        origin_competition_id: originCompetition.id,
        destination_competition_id: destinationCompetition.id,
        source: 'eurobasket',
        position: pos,
      });
      setResults(res.data);
    } catch (err) {
      console.error('Error checking performance:', err);
      setError(err.response?.data?.message || t('competitionPerformance.errors.calculating'));
    } finally {
      setLoading(false);
    }
  }, [originCompetition, destinationCompetition, t]);

  const handleCheck = () => fetchResults(positionFilter);

  const handlePositionChange = (e, newPos) => {
    setPositionFilter(newPos);
    // Auto-reload if we already have results
    if (originCompetition && destinationCompetition && results) {
      fetchResults(newPos);
    }
  };

  const getFactorColor = (f) => f > 0 ? '#4caf50' : f < 0 ? '#f44336' : '#9e9e9e';
  const getFactorBgColor = (f) => f > 0 ? 'rgba(76,175,80,0.1)' : f < 0 ? 'rgba(244,67,54,0.1)' : 'rgba(158,158,158,0.1)';
  const fmt = (v) => (v === null || v === undefined || v === 0) ? '-' : Number(v).toFixed(1);

  const renderGroupHeader = (params) => (
    <li key={params.key}>
      <Box sx={{
        position: 'sticky', top: -8, zIndex: 1,
        px: 1.5, py: 0.5,
        backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f0f0f0',
        borderBottom: '1px solid',
        borderColor: 'divider',
        fontWeight: 700,
        fontSize: '0.78rem',
        color: 'text.secondary',
        letterSpacing: 0.3,
      }}>
        {params.group}
      </Box>
      <ul style={{ padding: 0 }}>{params.children}</ul>
    </li>
  );

  return (
    <>
      <LoadingBackdrop open={loading} />
      <Box sx={{ py: 2, px: { xs: 0.5, sm: 1, md: 0 } }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          <CompareArrowsIcon sx={{ mr: 1, verticalAlign: 'bottom', color: '#F25F29' }} />
          {t('competitionPerformance.title')}
        </Typography>

        {/* Form */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5} md={4}>
              <Autocomplete
                options={apiCompetitions}
                groupBy={(opt) => getCompGroup(opt.name)}
                getOptionLabel={(opt) => `${opt.name} (${opt.nb_players})`}
                value={originCompetition}
                onChange={(e, v) => setOriginCompetition(v)}
                loading={loadingCompetitions}
                renderGroup={renderGroupHeader}
                renderInput={(params) => (
                  <TextField {...params} label={t('competitionPerformance.origin')} size="small"
                    InputProps={{ ...params.InputProps, endAdornment: (<>{loadingCompetitions ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }}
                  />
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
              <ArrowForwardIcon sx={{ color: '#F25F29', fontSize: 28 }} />
            </Grid>
            <Grid item xs={12} sm={5} md={4}>
              <Autocomplete
                options={apiCompetitions}
                groupBy={(opt) => getCompGroup(opt.name)}
                getOptionLabel={(opt) => `${opt.name} (${opt.nb_players})`}
                value={destinationCompetition}
                onChange={(e, v) => setDestinationCompetition(v)}
                loading={loadingCompetitions}
                renderGroup={renderGroupHeader}
                renderInput={(params) => (
                  <TextField {...params} label={t('competitionPerformance.destination')} size="small"
                    InputProps={{ ...params.InputProps, endAdornment: (<>{loadingCompetitions ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }}
                  />
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
              <Button variant="contained" fullWidth onClick={handleCheck}
                disabled={!originCompetition || !destinationCompetition || loading}
                sx={{ backgroundColor: '#F25F29', '&:hover': { backgroundColor: '#d94f1e' }, fontWeight: 'bold', height: 40 }}
              >
                {t('competitionPerformance.check')}
              </Button>
            </Grid>
          </Grid>

          {/* Position filter */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('competitionPerformance.position')}:
            </Typography>
            <ToggleButtonGroup value={positionFilter} exclusive onChange={handlePositionChange} size="small">
              {POSITIONS.map((pos) => (
                <ToggleButton key={pos.value || 'all'} value={pos.value}
                  sx={{
                    textTransform: 'none', px: 1.5, py: 0.3, fontSize: '0.8rem',
                    '&.Mui-selected': { backgroundColor: '#F25F29', color: '#fff', '&:hover': { backgroundColor: '#d94f1e' } },
                  }}
                >
                  {pos.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Results */}
        {results && (
          <>
            {results.sample_size === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>{t('competitionPerformance.noPlayersFound')}</Alert>
            ) : (
              <>
                {/* Stats Table */}
                <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                  <Box sx={{
                    p: 1.5, backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1
                  }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TrophyIcon sx={{ color: '#F25F29' }} />
                      <Typography variant="subtitle1" fontWeight="bold">{results.origin_competition}</Typography>
                      <ArrowForwardIcon sx={{ color: '#F25F29' }} />
                      <Typography variant="subtitle1" fontWeight="bold">{results.destination_competition}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip icon={<PersonIcon />} label={t('competitionPerformance.infoPlayers', { count: results.unique_players })} color="primary" variant="outlined" size="small" />
                      <Chip icon={<BasketballIcon />} label={t('competitionPerformance.infoGames', { origin: results.avg_games_origin, destination: results.avg_games_destination })} variant="outlined" size="small" />
                      <Chip icon={<CalendarIcon />} label={results.seasons_covered?.join(', ')} variant="outlined" size="small" />
                    </Stack>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 'bold', width: 100 }}>{t('competitionPerformance.stat')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>{t('competitionPerformance.originLabel')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('competitionPerformance.factor')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#F25F29' }}>{t('competitionPerformance.destinationLabel')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results.stats.map((stat) => (
                          <TableRow key={stat.name} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{stat.name}</TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.9rem' }}>{stat.origin_avg}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${stat.factor > 0 ? '+' : ''}${stat.factor}%`} size="small"
                                sx={{ backgroundColor: getFactorBgColor(stat.factor), color: getFactorColor(stat.factor), fontWeight: 'bold', fontSize: '0.8rem', minWidth: 70 }}
                                icon={stat.factor > 0
                                  ? <TrendingUpIcon sx={{ fontSize: 16, color: '#4caf50 !important' }} />
                                  : stat.factor < 0 ? <TrendingDownIcon sx={{ fontSize: 16, color: '#f44336 !important' }} /> : undefined}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.9rem' }}>{stat.destination_avg}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                {/* Players Table */}
                <Paper sx={{ overflow: 'hidden' }}>
                  <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      <PersonIcon sx={{ mr: 1, verticalAlign: 'bottom', color: '#F25F29' }} />
                      {t('competitionPerformance.playersPanel', { count: results.unique_players })}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('competitionPerformance.playerName')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('competitionPerformance.position')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('competitionPerformance.height')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('competitionPerformance.nationality')}</TableCell>
                          {/* Origin */}
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2', borderLeft: '2px solid #e0e0e0' }}>{t('competitionPerformance.originLabel')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.7rem' }}>Pts</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.7rem' }}>Reb</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.7rem' }}>Ast</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.7rem' }}>Eff</TableCell>
                          {/* Destination */}
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#F25F29', borderLeft: '2px solid #e0e0e0' }}>{t('competitionPerformance.destinationLabel')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#F25F29', fontSize: '0.7rem' }}>Pts</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#F25F29', fontSize: '0.7rem' }}>Reb</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#F25F29', fontSize: '0.7rem' }}>Ast</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: '#F25F29', fontSize: '0.7rem' }}>Eff</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results.players.map((player) => {
                          const n = player.passages.length;
                          const oAvg = {
                            pts: player.passages.reduce((s, p) => s + (p.stats_origin?.pts || 0), 0) / n,
                            reb: player.passages.reduce((s, p) => s + (p.stats_origin?.reb || 0), 0) / n,
                            ast: player.passages.reduce((s, p) => s + (p.stats_origin?.ast || 0), 0) / n,
                            eff: player.passages.reduce((s, p) => s + (p.stats_origin?.eff || 0), 0) / n,
                          };
                          const dAvg = {
                            pts: player.passages.reduce((s, p) => s + (p.stats_destination?.pts || 0), 0) / n,
                            reb: player.passages.reduce((s, p) => s + (p.stats_destination?.reb || 0), 0) / n,
                            ast: player.passages.reduce((s, p) => s + (p.stats_destination?.ast || 0), 0) / n,
                            eff: player.passages.reduce((s, p) => s + (p.stats_destination?.eff || 0), 0) / n,
                          };
                          const isLocal = player.is_local && player.local_id;
                          return (
                            <TableRow key={player.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Avatar
                                    src={isLocal ? getPlayerPhotoUrl(player.local_id) : undefined}
                                    sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                                  >
                                    {(player.nom || '?').charAt(0)}
                                  </Avatar>
                                  {isLocal ? (
                                    <Typography
                                      component="a"
                                      href={`/players/${player.local_id}`}
                                      target="_blank"
                                      variant="body2"
                                      sx={{
                                        fontWeight: 'bold',
                                        textDecoration: 'none',
                                        color: 'primary.main',
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      {player.nom}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" fontWeight="bold">{player.nom}</Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="center">
                                {player.poste && (
                                  <Chip
                                    label={translateEurobasketPos(player.poste, t) || player.poste}
                                    size="small"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">{player.taille ? `${player.taille}cm` : '-'}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">{player.nationalite || '-'}</Typography>
                              </TableCell>
                              {/* Origin */}
                              <TableCell align="center" sx={{ borderLeft: '2px solid #e0e0e0' }}>
                                {player.passages.map((p, i) => (
                                  <Tooltip key={i} title={`${p.team_origin || '?'} (${p.games_origin} ${t('competitionPerformance.games')})`}>
                                    <Chip label={`${p.season_origin} ${p.team_origin || ''}`} size="small" variant="outlined"
                                      sx={{ fontSize: '0.55rem', height: 18, m: 0.2, borderColor: '#1976d2', color: '#1976d2', maxWidth: 150 }} />
                                  </Tooltip>
                                ))}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(oAvg.pts)}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(oAvg.reb)}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(oAvg.ast)}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(oAvg.eff)}</TableCell>
                              {/* Destination */}
                              <TableCell align="center" sx={{ borderLeft: '2px solid #e0e0e0' }}>
                                {player.passages.map((p, i) => (
                                  <Tooltip key={i} title={`${p.team_destination || '?'} (${p.games_destination} ${t('competitionPerformance.games')})`}>
                                    <Chip label={`${p.season_destination} ${p.team_destination || ''}`} size="small" variant="outlined"
                                      sx={{ fontSize: '0.55rem', height: 18, m: 0.2, borderColor: '#F25F29', color: '#F25F29', maxWidth: 150 }} />
                                  </Tooltip>
                                ))}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(dAvg.pts)}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(dAvg.reb)}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(dAvg.ast)}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{fmt(dAvg.eff)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
}

export default CompetitionPerformance;
